import { STORE_KEY_ID_LIST, storeGet } from 'mailup-react-helpers'

import {
  beRecipientApiGetGroupDetails,
  beRecipientApiGetGroupsOverlapRecipients,
  beRecipientApiGetGroupsStats,
  beRecipientApiGetListOfGroupDetails,
} from 'Api/beRecipientApiTS'
import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'
import type { GroupDetails } from 'Types/beRecipientsApi'
import type { LabelValuePair } from 'Types/common'

import type { ResultItem } from 'Pages/GroupsOverlap/model'

export const toLabelValue = (o: GroupDetails): LabelValuePair => ({
  value: o.Id,
  label: `${o.Id} - ${o.Name}`,
})

export const mergeGroups = async (
  items: LabelValuePair[],
  selectedGroups: number[],
): Promise<LabelValuePair[]> => {
  const newItems = await selectedGroups.reduce<Promise<LabelValuePair[]>>(
    async (acc, idGroup) => {
      const prevItems = await acc

      const idx = prevItems.findIndex((g) => g.value === idGroup)

      if (idx === -1) {
        //  selected group not in starting list: fetch and add
        const groupDetails = await beRecipientApiGetGroupDetails(idGroup)
        if (groupDetails !== null) {
          return [toLabelValue(groupDetails), ...prevItems]
        }
      } else {
        //  push selected on top
        return [
          ...prevItems.slice(idx, idx + 1),
          ...prevItems.slice(0, idx),
          ...prevItems.slice(idx + 1),
        ]
      }

      return prevItems
    },
    Promise.resolve(items),
  )

  return newItems
}

export const getGroups = async (
  searchBy: string,
  selectedGroups: number[],
): Promise<{ items: LabelValuePair[]; itemsExceed: boolean }> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  const { Items, TotalItems } = await beRecipientApiGetListOfGroupDetails(
    idList,
    {
      searchType: searchBy.length > 0 ? 'CONTAINS' : undefined,
      searchBy: searchBy.length > 0 ? searchBy : undefined,
      pageNumber: 1,
      pageSize: INPUT_MESSAGES_MAX_ITEMS,
      orderBy: 'Id',
      orderDescending: true,
    },
  )

  return {
    items: await mergeGroups(Items.map(toLabelValue), selectedGroups),
    itemsExceed: TotalItems > INPUT_MESSAGES_MAX_ITEMS,
  }
}

export const getRecipientsCount = async (idGroup: number): Promise<number> => {
  const result = await beRecipientApiGetGroupsStats([idGroup])
  return result[0].EmailSubscribed
}

export const fetchGroups = async (
  searchTerm: string,
): Promise<{
  items: Array<{ label: string; value: number }>
  totalItems: number
}> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  const { Items, TotalItems } = await beRecipientApiGetListOfGroupDetails(
    idList,
    {
      searchType: searchTerm.length > 0 ? 'CONTAINS' : undefined,
      searchBy: searchTerm.length > 0 ? searchTerm : undefined,
      //  hasRecipients: 'HASEMAILRECIPIENTS',
      //  subscriberStatus: 'SUBSCRIBED',
      orderBy: 'Id',
      orderDescending: true,
      pageNumber: 1,
      pageSize: INPUT_MESSAGES_MAX_ITEMS,
    },
  )

  return {
    items: Items.map(({ Id, Name }: { Id: number; Name: string }) => ({
      value: Id,
      label: `${Id} - ${Name}`,
    })),
    totalItems: TotalItems,
  }
}

export const fetchGroupById = async (
  id: number,
): Promise<{ label: string; value: number } | undefined> => {
  const res = await beRecipientApiGetGroupDetails(id)

  if (res !== null && res !== undefined) {
    return {
      value: res.Id,
      label: `${res.Id} - ${res.Name}`,
    }
  }

  return undefined
}

export const getGroupsOverlap = async (
  from: number,
  to: number[],
): Promise<ResultItem[]> => {
  const groupsOverlap = await beRecipientApiGetGroupsOverlapRecipients({
    IdGroup: from,
    OverlapGroupIds: to,
  })

  return await groupsOverlap
    .filter((g) => g.Subscribers > 0)
    .reduce<Promise<ResultItem[]>>(async (acc, go) => {
      const curr = await acc

      const EmailSubscribed = await getRecipientsCount(go.IdGroup)

      return [
        ...curr,
        {
          id: go.IdGroup,
          idGroupFrom: from,
          name: go.Name,
          subscribers: EmailSubscribed,
          overlap: go.Subscribers,
        },
      ]
    }, Promise.resolve([]))
}
